import React, { useState, useMemo } from 'react';
import { formatDate, formatMoney } from '../../utils/constants';

// 날짜 변환 함수 (YYMMDD → YYYY-MM-DD)
const parseSimpleDate = (input) => {
  if (!input || input.length !== 6) return '';
  const yy = input.substring(0, 2);
  const mm = input.substring(2, 4);
  const dd = input.substring(4, 6);
  return `20${yy}-${mm}-${dd}`;
};

function SalesReport({ token, materials = [], records = [] }) {
  // 기간 필터
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  // 날짜 입력 핸들러
  const handleStartDateChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setStartDateInput(value);
    if (value.length === 6) {
      setStartDate(parseSimpleDate(value));
    } else {
      setStartDate('');
    }
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').substring(0, 6);
    setEndDateInput(value);
    if (value.length === 6) {
      setEndDate(parseSimpleDate(value));
    } else {
      setEndDate('');
    }
  };

  // 기간별 매입 계산
  const purchaseData = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        supplyPrice: 0,
        vat: 0,
        total: 0,
        count: 0
      };
    }

    const filtered = materials.filter(m => {
      if (!m.in_date) return false;
      return m.in_date >= startDate && m.in_date <= endDate;
    });

    const supplyPrice = filtered.reduce((sum, m) => {
      const supply = m.purchase_supply_price || (m.unit_price ? Math.floor(m.unit_price / 1.1) : 0);
      const quantity = m.quantity || 0;
      return sum + (supply * quantity);
    }, 0);

    const vat = filtered.reduce((sum, m) => {
      const vatAmount = m.purchase_vat || (m.unit_price ? m.unit_price - Math.floor(m.unit_price / 1.1) : 0);
      const quantity = m.quantity || 0;
      return sum + (vatAmount * quantity);
    }, 0);

    return {
      supplyPrice,
      vat,
      total: supplyPrice + vat,
      count: filtered.length
    };
  }, [materials, startDate, endDate]);

  // 기간별 매출 계산
  const salesData = useMemo(() => {
    if (!startDate || !endDate) {
      return {
        supplyPrice: 0,
        vat: 0,
        total: 0,
        count: 0
      };
    }

    const filtered = records.filter(r => {
      if (!r.receipt_date) return false;
      return r.receipt_date >= startDate && r.receipt_date <= endDate;
    });

    const supplyPrice = filtered.reduce((sum, r) => {
      return sum + (parseInt(r.supply_amount) || 0);
    }, 0);

    const vat = filtered.reduce((sum, r) => {
      return sum + (parseInt(r.vat_amount) || 0);
    }, 0);

    const total = filtered.reduce((sum, r) => {
      const cost = parseFloat(r.total_cost) || 0;
      // vat_included가 true면 부가세 포함, false면 공급가만
      return sum + (r.vat_included ? Math.floor(cost * 1.1) : cost);
    }, 0);

    return {
      supplyPrice,
      vat,
      total,
      count: filtered.length
    };
  }, [records, startDate, endDate]);

  // 순액 계산 (매출 - 매입)
  const netAmount = useMemo(() => {
    return {
      supplyPrice: salesData.supplyPrice - purchaseData.supplyPrice,
      vat: salesData.vat - purchaseData.vat,
      total: salesData.total - purchaseData.total
    };
  }, [salesData, purchaseData]);

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #3498db' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#2c3e50' }}>
          기간별 매입/매출 현황
        </h2>
      </div>

      {/* 기간 선택 */}
      <div style={{
        padding: '15px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '20px',
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <label style={{ fontWeight: 'bold', fontSize: '14px' }}>기간:</label>
        <input
          type="text"
          value={startDateInput}
          onChange={handleStartDateChange}
          placeholder="YYMMDD"
          style={{
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            width: '90px',
            fontSize: '14px'
          }}
          maxLength="6"
        />
        <span style={{ margin: '0 5px' }}>~</span>
        <input
          type="text"
          value={endDateInput}
          onChange={handleEndDateChange}
          placeholder="YYMMDD"
          style={{
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            width: '90px',
            fontSize: '14px'
          }}
          maxLength="6"
        />
        {startDate && endDate && (
          <span style={{ fontSize: '14px', color: '#7f8c8d' }}>
            ({formatDate(startDate)} ~ {formatDate(endDate)})
          </span>
        )}
        <button
          onClick={() => {
            setStartDate('');
            setEndDate('');
            setStartDateInput('');
            setEndDateInput('');
          }}
          style={{
            padding: '8px 16px',
            backgroundColor: '#95a5a6',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          초기화
        </button>
      </div>

      {/* 통계 카드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '20px',
        marginBottom: '20px'
      }}>
        {/* 매입 카드 */}
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#e74c3c', fontWeight: 'bold' }}>
            매입
          </h3>
          <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '10px' }}>
            건수: <strong style={{ color: '#2c3e50' }}>{purchaseData.count}건</strong>
          </div>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#7f8c8d' }}>공급가:</span>
            <strong style={{ color: '#2c3e50' }}>{formatMoney(purchaseData.supplyPrice)}</strong>
          </div>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#7f8c8d' }}>부가세:</span>
            <strong style={{ color: '#2c3e50' }}>{formatMoney(purchaseData.vat)}</strong>
          </div>
          <div style={{
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: '2px solid #e74c3c',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#e74c3c' }}>합계:</span>
            <strong style={{ fontSize: '18px', color: '#e74c3c' }}>
              {formatMoney(purchaseData.total)}
            </strong>
          </div>
        </div>

        {/* 매출 카드 */}
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#27ae60', fontWeight: 'bold' }}>
            매출
          </h3>
          <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '10px' }}>
            건수: <strong style={{ color: '#2c3e50' }}>{salesData.count}건</strong>
          </div>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#7f8c8d' }}>공급가:</span>
            <strong style={{ color: '#2c3e50' }}>{formatMoney(salesData.supplyPrice)}</strong>
          </div>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#7f8c8d' }}>부가세:</span>
            <strong style={{ color: '#2c3e50' }}>{formatMoney(salesData.vat)}</strong>
          </div>
          <div style={{
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: '2px solid #27ae60',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#27ae60' }}>합계:</span>
            <strong style={{ fontSize: '18px', color: '#27ae60' }}>
              {formatMoney(salesData.total)}
            </strong>
          </div>
        </div>

        {/* 순액 카드 */}
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#3498db', fontWeight: 'bold' }}>
            순액 (매출 - 매입)
          </h3>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#7f8c8d' }}>공급가:</span>
            <strong style={{
              color: netAmount.supplyPrice >= 0 ? '#27ae60' : '#e74c3c'
            }}>
              {formatMoney(netAmount.supplyPrice)}
            </strong>
          </div>
          <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#7f8c8d' }}>부가세:</span>
            <strong style={{
              color: netAmount.vat >= 0 ? '#27ae60' : '#e74c3c'
            }}>
              {formatMoney(netAmount.vat)}
            </strong>
          </div>
          <div style={{
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: '2px solid #3498db',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#3498db' }}>합계:</span>
            <strong style={{
              fontSize: '18px',
              color: netAmount.total >= 0 ? '#27ae60' : '#e74c3c'
            }}>
              {formatMoney(netAmount.total)}
            </strong>
          </div>
        </div>
      </div>

      {/* 안내 메시지 */}
      {(!startDate || !endDate) && (
        <div style={{
          padding: '20px',
          backgroundColor: '#ecf0f1',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#7f8c8d'
        }}>
          기간을 선택하면 매입/매출 현황을 확인할 수 있습니다.
        </div>
      )}
    </div>
  );
}

export default SalesReport;
